import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from "react-router-dom";

import AppContext from "../../app-context";
import { socketUseEffect } from "../../socket";

import PageTitle from "../core/page-title";

import SideBar from "../core/dashboards/sidebar";
import MainDashboardContent from "../core/dashboards/section";

import Dashboard from "./sub-pages/web-sites/dashboard";
import Status from "./sub-pages/web-sites/status";
import Tests from "./sub-pages/web-sites/tests";
import Uptime from "./sub-pages/web-sites/uptime";
import { useAuth } from "../../AuthContext";
import { IconEdit, IconLayoutDashboard, IconPalette, IconPlug, IconTrash } from "@tabler/icons-react";
import WebSites from "./web_sites";

// ---- i18n ----
const dict = {
    en: {
        loading: "Loading...",
        titlePrefix: "Web Sites",
        subTitle: "Details",
        listLink: "Web Sites",

        tabs: {
            dashboard: "Dashboard",
            statusperiod: "Status Period",
            latesttests: "Latest Tests",
            uptimechecks: "Uptime Checks",
            edit: "Edit",
            delete: "Remove",
        },

        sort: {
            status: "Status",
            name: "Name",
            created: "Created Date",
        }
    },

    ru: {
        loading: "Загрузка...",
        titlePrefix: "Веб-сайты",
        subTitle: "Детали",
        listLink: "Веб-сайты",

        tabs: {
            dashboard: "Дашборд",
            statusperiod: "Период статуса",
            latesttests: "Последние тесты",
            uptimechecks: "Проверки аптайма",
            edit: "Редактировать",
            delete: "Удалить",
        },

        sort: {
            status: "Статус",
            name: "Название",
            created: "Дата создания",
        }
    },

    ua: {
        loading: "Завантаження...",
        titlePrefix: "Веб-сайти",
        subTitle: "Деталі",
        listLink: "Веб-сайти",

        tabs: {
            dashboard: "Дашборд",
            statusperiod: "Період статусу",
            latesttests: "Останні тести",
            uptimechecks: "Перевірки аптайму",
            edit: "Редагувати",
            delete: "Видалити",
        },

        sort: {
            status: "Статус",
            name: "Назва",
            created: "Дата створення",
        }
    },
};

const getInitialLang = () =>
    (localStorage.getItem("lang") || "en").toLowerCase();

// ---- component ----
const WebSiteDashboard = ({ lang: langProp, ...props }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const [details, setDetails] = useState(() => ({ name: t.loading }));
    const [statuses, setStatuses] = useState([]);
    const [monitoringTotal, setMonitoringTotal] = useState([]);
    const [countdown, setCountdown] = useState(null);
    const [nextUpdate, setNextUpdate] = useState(null);
    const { id, page } = useParams();

    const [refresh, setRefresh] = useState(true);

    const { user } = useAuth();
    const [statusPeriods, setStatusPeriods] = useState([]);

    const listeners = [
        { name: "websites", fn: "get", args: { user_id: user.user_id, id: id }, saveDataFN: setDetails, row: "0" },
        { name: "websites", fn: "statuses", args: { user_id: user.user_id, id: id }, saveDataFN: (res) => setStatuses(res.data || []) },
        { name: "websites", fn: "statusperiod", args: { user_id: user.user_id, id: id }, saveDataFN: (res) => setStatusPeriods(res.data || []) },
        { name: "websites", fn: "monitoringtotal", args: { user_id: user.user_id, id: id }, saveDataFN: (res) => setMonitoringTotal(res.data || []) },
    ];
    useEffect(() => {
        const cleanup = socketUseEffect(listeners, user);
        return cleanup;
    }, [id, page, refresh, user]);

    // запуск отсчёта только когда наступило окно nextUpdate (когда countdown > 0)
    useEffect(() => {
        let timer;
        if (countdown !== null && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev === null) return null;
                    if (prev <= 1) {
                        setRefresh((r) => !r);
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [countdown]);

    // вычисляем время до следующего обновления по load_interval
    useEffect(() => {
        let startTimer;
        const raw = details.load_interval;
        let intervalMinutes = null;
        if (raw !== undefined && raw !== null) {
            if (typeof raw === "string" && raw.startsWith("*/")) {
                const val = Number(raw.replace("*/", ""));
                if (!Number.isNaN(val)) intervalMinutes = val;
            } else {
                const val = Number(raw);
                if (!Number.isNaN(val)) intervalMinutes = val;
            }
        }
        if (!intervalMinutes || intervalMinutes <= 0) intervalMinutes = 1;

        const intervalMs = intervalMinutes * 60 * 1000;
        const now = Date.now();
        const nextAligned = Math.ceil(now / intervalMs) * intervalMs;
        setNextUpdate(new Date(nextAligned));

        // сбрасываем отсчёт и запускаем его только в момент наступления next update
        setCountdown(null);
        const startCountdown = () => setCountdown(30);
        const diffMs = nextAligned - now;
        if (diffMs <= 0) {
            startCountdown();
        } else {
            startTimer = setTimeout(startCountdown, diffMs);
        }

        return () => {
            if (startTimer) clearTimeout(startTimer);
        };
    }, [details.load_interval, refresh]);

    const tabs = [
        { label: t.tabs.dashboard,   page: "dashboard",   link: "/web-site/:id/dashboard",   controller: Dashboard, args: { details, statuses, countdown, nextUpdate } },
        { label: t.tabs.statusperiod,page: "statusperiod",link: "/web-site/:id/statusperiod",controller: Status, args: { statusPeriods } },
        { label: t.tabs.latesttests, page: "latesttests", link: "/web-site/:id/latesttests", controller: Tests, args: { statuses, monitoringTotal } },
        { label: t.tabs.uptimechecks,page: "uptimechecks",link: "/web-site/:id/uptimechecks",controller: Uptime, args: { monitoringTotal } },
        { label: t.tabs.edit,        page: "edit",        link: "/web-site/:id/edit",        controller: WebSites, args: { edit: 1, id, details, refresh, setRefresh } },
        { label: t.tabs.delete,      page: "delete",      link: "/web-site/:id/delete",      controller: WebSites, args: { delete: 1, id, details, refresh, setRefresh }  },
    ];

    const icons = {
        dashboard:   <IconLayoutDashboard size={20} />,
        statusperiod:<IconPalette size={20} />,
        latesttests: <IconPlug size={20} />,
        uptimechecks:<IconPlug size={20} />,
        edit:        <IconEdit size={20} />,
        delete:      <IconTrash size={20} />,
    };

    const sort = [
        { label: t.sort.status,  column: "status" },
        { label: t.sort.name,    column: "name" },
        { label: t.sort.created, column: "created_date" },
    ];

    return (
        <>
            <PageTitle
                title={
                    <>
                        {t.titlePrefix}: {details.name === t.loading ? <span className="loading-text">{t.loading}</span> : details.name}
                    </>
                }
                homeUrl={"/dashboard"}
                subTitle={t.subTitle}
                links={[{ name: t.listLink, link: "/web-sites" }]}
                menuItems={tabs}
                menuIcons={icons}
                curPage={page}
                curID={id}
            />
            <div className={"card overflow-hidden invoice-application"}>
                <div className="d-flex">
                    <div className="w-25 d-none d-lg-block border-end user-chat-box">
                        <div className="app-invoice">
                            <ul className="overflow-auto invoice-users simplebar-scrollable-y" data-simplebar="init">
                                <div className="simplebar-wrapper" style={{ margin: "0px" }}>
                                    <div className="simplebar-height-auto-observer-wrapper">
                                        <div className="simplebar-height-auto-observer"></div>
                                    </div>
                                    <div className="simplebar-mask">
                                        <div className="simplebar-offset" style={{ right: "0px", bottom: "0px" }}>
                                            <div
                                                className="simplebar-content-wrapper"
                                                tabIndex="0"
                                                role="region"
                                                aria-label="scrollable content"
                                                style={{ height: "100%", overflow: "hidden scroll" }}
                                            >
                                                <div className="simplebar-content" style={{ padding: "0px" }}>
                                                    <SideBar
                                                        id={id}
                                                        sort={sort}
                                                        object={"websites"}
                                                        path={"web-site"}
                                                        controller={WebSites}
                                                        refresh={refresh}
                                                        setRefresh={setRefresh}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="simplebar-placeholder" style={{ width: "280px", height: "459px" }}></div>
                                </div>
                                <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
                                    <div className="simplebar-scrollbar" style={{ width: "0px", display: "none" }}></div>
                                </div>
                                <div className="simplebar-track simplebar-vertical" style={{ visibility: "visible" }}>
                                    <div
                                        className="simplebar-scrollbar"
                                        style={{ height: "25px", display: "block", transform: "translate3d(0px, 0px, 0px)" }}
                                    ></div>
                                </div>
                            </ul>
                        </div>
                    </div>
                    <div className="w-75 w-xs-100 chat-container">
                        <div className="invoice-inner-part h-100">
                            <div className="invoiceing-box">
                                <div id="custom-invoice">
                                    <MainDashboardContent tabs={tabs} page={page} id={id} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WebSiteDashboard;
